import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  CellControllerKind,
  InputStringControllerEntity,
} from 'entities/TableControllers';
import { debounce } from 'utils/debounce';

import { ITableControllerBase, TableControllerParams } from '../interface';

type PrivateFields = '_value';

const INPUT_CHANGE_DEBOUNCE_TIME = 1400;

export default class TableInputStringController
  implements
    ITableControllerBase<InputStringControllerEntity['controller']['value']>
{
  constructor({
    record,
    onSave,
  }: TableControllerParams<InputStringControllerEntity>) {
    this._value = record.controller;
    this._initialValue = record.controller;

    makeObservable<TableInputStringController, PrivateFields>(this, {
      _value: observable.ref,
      controllerValue: computed,
      setValue: action.bound,

      toServerFormat: computed,

      reset: action.bound,
    });

    const onSaveHandler = debounce(async () => {
      await onSave();
      this._isUpdateBlocked = false;
    }, INPUT_CHANGE_DEBOUNCE_TIME);

    this._reactions.push(
      reaction(
        () => this._value,
        async () => {
          this._isUpdateBlocked = true;
          await onSaveHandler();
        }
      )
    );
  }

  private _value: InputStringControllerEntity['controller'];

  private readonly _reactions: IReactionDisposer[] = [];

  private readonly _initialValue: InputStringControllerEntity['controller'];

  private _isUpdateBlocked = false;

  get isUpdateBlocked(): boolean {
    return this._isUpdateBlocked;
  }

  get controllerKind() {
    return CellControllerKind.INPUT_STRING;
  }

  get controllerValue(): string {
    return this._value.value || '';
  }

  get toServerFormat() {
    return this._value.value === '' ? null : this._value.value;
  }

  reset(): void {
    this._value = this._initialValue;
  }

  destroy() {
    this._reactions.forEach((reaction) => reaction());
  }

  setValue(nextValue: string): void {
    if (nextValue !== this._value.value) {
      this._value = {
        value: nextValue,
      };
    }
  }
}
